import Layout from "../components/3_organism/layout/layout";
import Head from "next/head"
import Styles from './extras/styles/faq.module.scss'
import Link from 'next/link';
import { useState } from "react";
import DiscordServerButton from "../components/1_atom/buttons/discordServerButton";
import DiscordBotButton from "../components/1_atom/buttons/discordBotButton";
import Button from "../components/1_atom/buttons/button";
import { useRouter } from "next/router";
import { capitalize } from "functions";

type questionType = { type: "text", title: string, text: string} | { type: "jsx", title: string, content: () => JSX.Element }
type menuOption = {
  sectionName: string;
  questions: questionType[];
};

export default function FAQ() {

  const menuOptions: menuOption[] = [
    {
      sectionName: "website",
      questions: [
        {
          type: "jsx",
          title: "I can't login, help!",
          content: () => (
            <>
              <p>Here's a few things you can try:</p>
              <ul>
                <li><b>Use incognito mode.</b> By default extensions are turned off in incognito.</li>
                <li><b>Try another browser.</b> This is the fastest way to check if either extensions, bad cache or bad browser config is hindering you from logging in.</li>
                <li><b>Disable VPNs or proxies.</b> There has been some reports of people being able to login after disabling their VPN.</li>
                <li><b>Use a VPN.</b> You may be located in a country that is not serviced by Google (specifically Firebase Auth). If you are, try VPNing to a country that is.</li>
              </ul>
            </>
          )
        },
        {
          type: "jsx",
          title: "How do I switch to dark mode?",
          content: () => <p>You can choose your appearance preferences in <Link href="/settings" className="link">settings</Link>.<br /> It's set to follow your OS preferences by default, though this will only work with a browser that supports 'prefers-color-scheme'. If 'prefers-color-scheme' is not found the website will default to light appearance. Here's how to set dark mode in some modern OS: <span className={Styles.links}><a href="https://support.apple.com/en-us/HT208976" target="_blank">Macos Mojave+</a>, <a href="https://support.apple.com/en-au/HT210332" target="_blank">iOS 13+</a>, <a href="https://blogs.windows.com/windowsexperience/2016/08/08/windows-10-tip-personalize-your-pc-by-enabling-the-dark-theme/" target="_blank">Windows 10+</a>, <a href="https://developer.android.com/guide/topics/ui/look-and-feel/darktheme" target="_blank">Android 10+</a></span>.</p>
        },
        {
          type: "text",
          title: "What are roles?",
          text: "Roles are an optional middle step between your people and what you need them to do. Update the role and all the places you've used that role will update. We recommend using roles whenever possible.",
        },
        {
          type: "jsx",
          title: "Why does it look weird when I click 'only my assignments'?",
          content: () => <p>Admins can choose between three different ways of filtering. Horisontal, vertical or single cell. Horisontal will preserve the row where each user is mentioned, vertical will preserve each column and single cell will only preserve each cell.<br /> ℹ️ Note that the first column and the column headers will never be filtered away. Text and image panels will also never be filtered away.</p>
        },
        {
          type: "text",
          title: "I signed up, so why can't I see myself on the roster?",
          text: "Either the roster is already full or the signup is set to put signups on the bench automatically instead of directly onto the roster. The bench is normally hidden on the website and you cannot see it unless you're an admin.",
        },
        {
          type: "text",
          title: "What does the colored dots in roster mean?",
          text: "If your signup admin has enabled confirmations, all members will either show orange for not confirmed or green for confirmed. A blue dot means that member is set as standby.",
        },
      ],
    },
    {
      sectionName: "discord bot",
      questions: [
        {
          type: "jsx",
          title: "What can I do with the Discord bot?",
          content: () => (
            <div className="">
              <p className="mb1">
                Click the button below to be taken to a page explaining all about the bot. All information on what commands exist and how to use them will be found there.
              </p>
              <Link href="/bot">
                <Button text="See discord bot commands" />
              </Link>
            </div>
          ),
        },
        {
          type: "jsx",
          title: "How do I add the discord bot to my discord server?",
          content: () => <div className="">
            <p className="mb1">Click the button below. It will take you to a page to confirm access and select which server you want to add the bot to. Please note that you'll need manage server permission on the server you want to add it to.</p>
            <DiscordBotButton />
          </div>
        },
        {
          type: "jsx",
          title: "What Discord permissions does the bot need?",
          content: () => <p>The bot will ask for the permissions it needs when you add the bot. All Discord members with "Use Application commands" permission can create and link to existing signups, but only signup owner & admins can use /request confirmation.</p>
        },
        {
          type: "jsx",
          title: "How do I link to an existing signup?",
          content: () => <p>You can link a pre-existing signup by going to the correct channel and using the slash command <b>/signup link</b>. You'll be asked for the signup ID which you can find in the URL when you're on the signup page.</p>
        },
        {
          type: "jsx",
          title: "How do I create a new signup?",
          content: () => <p>You can create a new signup by using the slash command <b>/signup create</b>. Then provide the information as the command asks for.</p>
        },
        {
          type: "text",
          title: "Will signups on discord stay in sync with the website?",
          text: "If somebody signs up with the discord bot, those changes will be instantly visible on the website. If somebody signs up on the website, it will not automatically update any linked discord posts until someone interacts with them, though this is something we may improve in the future.",
        },
        {
          type: "text",
          title: "Why does the emojis not work?",
          text: "If you see the raw emoji codes, make sure to check 'use external emojis' for everyone in permissions. If it's already turned on, try disabling and re-enabling it. Yes, it seems sort of bugged.",
        },
      ],
    },
  ];

  const [selectedMenu, setSelectedMenu] = useState(menuOptions[0])

  const Question = ({ question }: { question: questionType}) => {

    const renderContent = () => {
      if(question.type === "jsx") return question.content()
      return <p>{question.text}</p>
    }

    return (
      <div className={`${Styles.question}`}>
        <h4>{question.title}</h4>
        {renderContent()}
      </div>
    );
  }

  const menu = (
    <div className={`${Styles.menu}`}>
      <h2 className="mb1">Frequently asked questions</h2>
      <h4 className="weak-text mb2">Can't find what you're looking for?<br /> Join our discord server!</h4>
      <h4 className="mb1">Table of contents</h4>
      <div className="flex-col gap1 mb3">
        {menuOptions.map((option) => (
          <h5
            key={option.sectionName}
            className={`${Styles.sectionName} ${selectedMenu.sectionName === option.sectionName ? Styles.active : ""}`}
            onClick={() => setSelectedMenu(option)}
          >
            {capitalize(option.sectionName)}
          </h5>
        ))}
      </div>
    </div>
  );
  
  const questions = (
    <article className={`${Styles.questions}`}>
      {selectedMenu.questions.map((question) => {
        return <Question key={question.title} question={question} />
      })}
    </article>
  );
  
  return (
    <Layout maxLayoutWidth="medium">
      <Head>
        <title>Frequently asked questions</title>
        <link rel="canonical" href={"https://prepi.io" + useRouter().pathname} />
        <meta name="description" content="Do you have a question about how to use Prepi? Here are the answers to some of our frequently asked questions." />
      </Head>
      <div className={`${Styles.content} flex-row`}>
        {menu}
        {questions}
      </div>
      <div className={Styles.discordServer}>
        <h5>Can't find what you're looking for?</h5>
        <p className="weak-text text-center">Feel free to ask any questions on our discord server!</p>
        <DiscordServerButton />
      </div>
    </Layout>
  );
}
